import {BaseElement, MsgLabel} from "back/commons/basis";
import {IItemUiEnv} from "back/wsp/views/itemMain";
import {IReg, REG} from "lib/commons/registry";
import {IView, IViewContainer, OViewVisitOptions, VIEWS} from "lib/commons/views";
import {DOM, JSX} from "lib/commons/xml/dom";
import {ORemoteViewInit} from "back/wsp/widgets/remoteView";
import {DOMSH} from "lib/commons/xml/domsh";
import {Action} from "lib/commons/actions";
import {Button} from "back/commons/widgets/buttons";
import {POPUP} from "back/commons/widgets/popups";
import {WspsGrid} from "back/wsp/widgets/wsp/wspsGrid";
import {IWspUiEnv, JWspInfoInList, JWspUriChangeMsg, WSP, Wsp} from "lib/wsp/wsp";
import {SkinClassStl, STYLER} from "lib/styler/styler";
import {IO, IResponse} from "lib/commons/io/io";
import {ESrcSt, JSrcFields, SRC, srcRef} from "lib/wsp/src";
import {FastFindItem, OFastFindItemsInit} from "back/wsp/dialogs/fastFindItem";
import {SearchAnd, SearchItemModel} from "lib/wsp/search";
import {ItemCreator} from "back/wsp/dialogs/itemCreator";
import {ItemType} from "lib/wsp/wspMetaUi";
import {ERROR} from "lib/core/errorReport";
import {SrcDrawerInline} from "back/wsp/widgets/srcDrawer";
import {Generators, OGeneratorsInit} from "back/wsp/views/generators";
import {ITEM, JGenInfo} from "lib/wsp/item";
import {IDatasItemDynGen, ItemDynGen, OItemDynGenInit} from "back/wsp/views/item/itemDynGen";
import {OTabsInit, Tabs} from "back/commons/widgets/tabs";
import {AreaAsync} from "lib/commons/areas";
import {EWspChangesEvts, WspsLivePlace} from "lib/wsp/wspsLive";
import {JWspOptionDef, JWspTypeDef, WSPPACK} from "lib/wsp/pack";


/**
 * View d'une res remote d'un item
 */

export interface OItemSkinClassStlTestInit extends ORemoteViewInit {
	/** Spécifié explicitement, ou repris dans le longDesc de l'item courant */
	skinClassStlCode?: string
	reg?: IReg<IItemUiEnv>
}

export interface OItemSkinClassPersisData {
	testWspCode?: string
	testSrcRef?: srcRef
}

export class ItemSkinClassStlTest extends BaseElement implements IView, IViewContainer {
	public reg: IReg<IItemUiEnv>;
	protected params: OItemSkinClassStlTestInit;
	protected msgElt: MsgLabel;
	protected contentElt: HTMLDivElement;

	protected testWspBtn: Button;
	protected testItemBtn: Button;

	view: IView;

	/** Wsp de test actuellement sélectionné */
	private testWsp: Wsp | null;
	private testWspReg: IReg<IWspUiEnv>;

	private testItem: JSrcFields | null;

	get skinClassStl(): SkinClassStl {
		return STYLER.getSkinClassStl(this.reg.env.wsp, this.params.skinClassStlCode);
	}

	get privateUri(): string {
		// FIXME : on enregistre et load ces datas dans une structure custom à la racine du privateFolder pour compat ODB (@see eu.scenari.wspodb.wsp.OdbWspDefinition.getPrivateDataFolder)
		return "/~styler/" + SRC.srcRef(this.reg.env.longDesc) + "/@testSkin.json";
	}

	_initialize(init: OItemSkinClassStlTestInit) {
		this.reg = this.findReg(init);
		this.params = init;
		if (!this.params.skinClassStlCode)
			this.params.skinClassStlCode = (this.reg.env.longDesc as any).itAttr_skClassStl;

		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);

		this.testWspBtn = <c-button id="selectWsp" ui-context="dialog" role="menu" onclick={this.findTestWsp}/> as Button;
		this.testWspBtn.addEventListener('keydown', async (ev: KeyboardEvent) => {
			if ((ev.key === 'Delete' || ev.key === 'Backspace') && this === DOMSH.findDocumentOrShadowRoot(this).activeElement) {
				await this.setTestWsp(null);
				ev.stopPropagation();
				return true;
			}
			return false;
		});

		this.testItemBtn = <c-button id="selectItem" ui-context="dialog" role="menu" onclick={this.findTestItem}/> as Button;
		this.testItemBtn.addEventListener('keydown', async (ev: KeyboardEvent) => {
			if ((ev.key === 'Delete' || ev.key === 'Backspace') && this === DOMSH.findDocumentOrShadowRoot(this).activeElement) {
				await this.setTestItem(null);
				ev.stopPropagation();
				return true;
			}
			return false;
		});

		const headerElt = sr.appendChild(<div id="headBar">
			<span class="headTi">Contexte de test :</span>
			<span id="ctxWsp">
				<label class="headSubTi">Atelier</label>
				{this.testWspBtn}
			</span>
			<span id="levelSeparator"/>
			<span id="ctxItem">
				<label class="headSubTi">Item</label>
				{this.testItemBtn}
			</span>
		</div>) as HTMLDivElement;

		this.msgElt = sr.appendChild(new MsgLabel().setStandardMsg("loading"));

		this.contentElt = sr.appendChild(<div id="contentBox" hidden/>) as HTMLDivElement;

		this.setTestWsp(null);

		this._onWspUriChange = this.onWspUriChange.bind(this);
		this.reg.env.place.eventsMgr.on("wspUriChange", this._onWspUriChange);
	}

	/**
	 * Affectation de l'atelier de test
	 */
	public async setTestWsp(wspCode: string, wspTitle?: string): Promise<void> {
		const wspTypesDefList = await WSPPACK.listWspTypesDef(this.reg.env.universe.packServer, [this.skinClassStl.skinClass(this.reg.env.wsp)?.class], true);
		const wspTypesKeys = wspTypesDefList.map(e => e.key);
		const wspTypesListTitle = wspTypesDefList.map(e => e.title).join(", ");
		let title = wspTitle || wspCode;

		if (wspCode) {
			if (this.testWsp && this.testWsp.code === wspCode)
				return;

			if (this.testWsp)
				this.setTestItem(null);

			this.testWspBtn.label = "...";

			const place = this.reg.env.universe.wspServer.wspsLive.newPlace();
			this.testWsp = place.getWsp(wspCode).listenChanges();
			this.testWspReg = REG.createSubRegMixed(this.reg, this.testWsp.reg);
			this.testWspReg.env.uiRoot = this;
			this.testWspReg.env.place = place;

			this.initTestWspCtx(this.testWspReg);
			this.setMsg(`Chargement de l'atelier '${title}'...`, "info", true);
			await this.testWsp.waitForAvailable();
			if (this.testWsp.wspTitle) title = this.testWsp.wspTitle;
			this.testWspBtn.label = title;
			if (this.testWsp.isAvailable) {
				let isWspAllowed = wspTypesKeys.includes(this.testWsp.infoWsp.wspType.key) || this.testWsp.infoWsp.wspOptions?.find((entry) => wspTypesKeys.includes(entry.key));
				if (isWspAllowed) {
					this.testWspBtn.disabled = false;
					DOM.setAttrBool(this.testWspBtn, "data-isEmpty", false);
					DOM.setAttrBool(this.testWspBtn, "data-isError", false);
					this.testItemBtn.disabled = false;
					await this.setTestItem(this.testItem ? SRC.srcRef(this.testItem) : null);
				} else {
					DOM.setAttrBool(this.testWspBtn, "data-isEmpty", false);
					DOM.setAttrBool(this.testWspBtn, "data-isError", true);
					this.testItemBtn.disabled = true;
					//await this.setTestItem(null, null, true);
					this.setMsg(`L'atelier '${title}' sélectionné n'est pas compatible avec ce skin.\nVeuillez sélectionner un atelier de test compatible avec l'un des modèles suivants : ${wspTypesListTitle}.`, "warning", true);
				}
			} else {
				DOM.setAttrBool(this.testWspBtn, "data-isEmpty", false);
				DOM.setAttrBool(this.testWspBtn, "data-isError", true);
				this.testItemBtn.disabled = true;
				await this.setTestItem(null, null, true);
				this.setMsg(`L'atelier '${title}' n'est pas dans un statut valide. Veuillez en corriger les propriétés.`, "warning", true);
			}
		} else {
			DOM.setAttrBool(this.testWspBtn, "data-isEmpty", true);
			DOM.setAttrBool(this.testWspBtn, "data-isError", false);
			// Btn item disabled + clear
			this.testItemBtn.disabled = true;
			await this.setTestItem(null, null, true);
			this.testWsp = null;
			this.contentElt.innerText = "";
			this.testWspBtn.label = "Sélectionner un atelier...";
			this.testItem = null;
			const skin = this.skinClassStl.skinClass(this.reg.env.wsp).name;
			this.setMsg(wspTypesListTitle ?
				`Veuillez sélectionner un atelier de test compatible avec l'un des modèles suivants : ${wspTypesListTitle}.`
				: `Veuillez installer un modèle documentaire exploitant le skin '${skin}'.`, "info", true);
		}
	}

	/**
	 * Affectation l'item de test
	 */
	public async setTestItem(srcRef: srcRef, srcFields?: JSrcFields, silent?: boolean): Promise<void> {
		// Générateurs de l'item
		const gensCodeList_item: Map<string, string> = new Map();
		// Générateurs interactifs
		const gensCodeList_dyn: Map<string, string> = new Map();

		if (silent === undefined && this.testItemBtn.disabled)
			silent = true;

		let wspTypesDefList: (JWspTypeDef | JWspOptionDef)[];

		function filterCtxGens(this: ItemSkinClassStlTest, itemGens: Map<string, string>, dynGens: Map<string, string>, wsp: Wsp, src?: JSrcFields) {
			if (wspTypesDefList) {
				for (const entry of wspTypesDefList) {
					if (!(wsp.infoWsp.wspType.key === entry.key || wsp.infoWsp.wspOptions?.findIndex(wspOption => wspOption.key === entry.key) > -1))
						continue;
					const skinClassGenerators = entry.generators?.filter((gen) => gen.skinClass === this.skinClassStl.class);
					if (skinClassGenerators) {
						skinClassGenerators.forEach((gen) => {
							if (!src || gen.rootModels?.indexOf(src.itModel) > -1) {
								if (gen.sgn.match(/.*#dyn\b.*/)) dynGens.set(gen.code, gen.name);
								else itemGens.set(gen.code, gen.name);
							}
						});
					}
				}
			}
		}

		if (this.testWsp) {
			wspTypesDefList = await WSPPACK.listWspTypesDef(this.reg.env.universe.packServer, [this.skinClassStl.skinClass(this.reg.env.wsp)?.class], true);
			filterCtxGens.call(this, gensCodeList_item, gensCodeList_dyn, this.testWsp);
		}

		let gensTitles = [...new Set(Array.of(...gensCodeList_item.values(), ...gensCodeList_dyn.values()))].sort().join(", ");

		if (this.testWsp && srcRef) {
			this.testItemBtn.innerText = "";
			DOM.setAttrBool(this.testItemBtn, "data-isEmpty", false);
			DOM.setAttrBool(this.testItemBtn, "data-isError", false);

			const srcDrawer = this.testItemBtn.appendChild(<SrcDrawerInline î={{
				reg: this.testWspReg,
				srcRef: srcRef,
			}}/>);
			const testItemSd = await this.testWsp.fetchShortDesc(srcRef);

			// Refresh de la liste des gens pour CE srcRef
			// Remarque : il peut rester des faux positifs (gen masqué coté UI (sub gen, gen pas dans un onglet ou conditionn à une perm, ...)
			gensCodeList_item.clear();
			gensCodeList_dyn.clear();
			filterCtxGens.call(this, gensCodeList_item, gensCodeList_dyn, this.testWsp, testItemSd);
			gensTitles = [...new Set(Array.of(...gensCodeList_item.values(), ...gensCodeList_dyn.values()))].sort().join(", ");

			if (testItemSd.srcSt === ESrcSt.file) {
				this.testItem = testItemSd;
				this.contentElt.innerText = "";
				if (!silent) this.setMsg("Chargement en cours...", "info", true);
				const tabs = this.contentElt.appendChild(<Tabs id="tabs" î={{
					vertical: true,
					skinOver: "styler-testSkin/tabs",
					tabSkinOver: "styler-testSkin/tab"
				} as OTabsInit<any>}/>) as Tabs;
				if (this.view) VIEWS.onViewHidden(this.view, true);
				this.view = tabs;

				// Tab 'dynGen'
				if (gensCodeList_dyn.size > 0) {
					const dynGenGenerators: IDatasItemDynGen[] = [];
					gensCodeList_dyn.forEach((value, key) => {
						dynGenGenerators.push({
							code: key,
							label: value,
							genProps: {
								skin: ITEM.GEN_SKIN_BY_URI_PREFIX + WSP.buildWspUri(this.reg.env.wsp.code, this.reg.env.longDesc.srcUri) + ";transform=facet&facet=resolveSkin",
							},
						});
					});
					await tabs.addTab(new AreaAsync<ItemSkinClassStlTest>()
						.setLabel(dynGenGenerators.length > 1 ? "Aperçus" : "Aperçu")
						.setDescription("Skin exploité par des publications dynamiques")
						.setBodyBuilder((ctx) => {
							return <ItemDynGen î={{
								reg: this.testWspReg,
								srcRef: ITEM.srcRefSub(testItemSd),
								generators: dynGenGenerators,
								toolbar: {
									skinSelector: false,
									refresh: true,
								},
								onViewBehaviour: "refresh",
							} as OItemDynGenInit}/>
						}), {select: true});
				}

				// Tab 'generators'
				if (gensCodeList_item.size > 0) {
					await tabs.addTab(new AreaAsync<ItemSkinClassStlTest>()
						.setLabel("Publications")
						.setDescription("Skin exploité par des publications")
						.setBodyBuilder((ctx) => {
							const gensBox = <Generators î={{
								reg: this.testWspReg,
								pubNodeMatchFilter: (entry) => {
									if(entry.genNodeType === 'dir') return true;
									const idx = entry.codeGenStack.lastIndexOf('_');
									const cdGen = idx < 0 ? entry.codeGenStack : entry.codeGenStack.substring(idx + 1);
									return gensCodeList_item.has(cdGen);
								},
								customDestPath: WSP.buildWspUri(this.reg.env.wsp.code, this.reg.env.longDesc.srcUri + '/' + WSP.buildWspUri(this.testWsp.code, this.testItem.srcUri)),
							} as OGeneratorsInit}/> as Generators;
							gensBox.setSrc(testItemSd);
							return gensBox;
						}), {select: gensCodeList_dyn.size === 0});
				}
				if (tabs.hasTabs()) {
					if (!silent) this.setMsg(null, null, false);
				} else if (!silent) this.setMsg(gensTitles ? `Veuillez sélectionner un item porteur d'un générateur à tester : ${gensTitles}.` : "Veuillez sélectionner un item porteur d'un générateur à tester.", "warning", true);
			} else {
				DOM.setAttrBool(this.testItemBtn, "data-isEmpty", false);
				DOM.setAttrBool(this.testItemBtn, "data-isError", true);
				if (!silent) this.setMsg(`L'item sélectionné est invalide.`, "warning", true);
			}
		} else {
			DOM.setAttrBool(this.testItemBtn, "data-isEmpty", true);
			DOM.setAttrBool(this.testItemBtn, "data-isError", false);
			this.testItemBtn.innerText = "Sélectionner un item...";
			this.testItem = null;
			if (!silent) this.setMsg(gensTitles ? `Veuillez sélectionner un item porteur d'un générateur à tester : ${gensTitles}.` : "Veuillez sélectionner un item porteur d'un générateur à tester.", "info", true);
		}
	}

	private async findTestItem(this: HTMLButtonElement, ev: Event) {
		try {
			if (this.disabled) return;

			const me = DOMSH.findHost<ItemSkinClassStlTest>(this);
			const wspTypesDefList = await WSPPACK.listWspTypesDef(me.reg.env.universe.packServer, [me.skinClassStl.skinClass(me.reg.env.wsp)?.class], true);

			const rootModelsKeys: Set<string> = new Set();

			if (me.testWsp) {
				wspTypesDefList.forEach((entry) => {
					const skinClassGenerators = entry.generators?.filter((gen) => gen.skinClass === me.skinClassStl.class);
					if (skinClassGenerators && (me.testWsp.infoWsp.wspType.key === entry.key || me.testWsp.infoWsp.wspOptions?.findIndex(wspOption => wspOption.key === entry.key) > -1)) {
						skinClassGenerators?.forEach((gen) => {
							gen.rootModels?.forEach(md => rootModelsKeys.add(md))
						});
					}
				});
			}

			const ct = new FastFindItem().initialize({
				reg: me.testWsp.reg,
				restrictions: new SearchAnd(
					new SearchItemModel(Array.from(rootModelsKeys))
				),
				emptyBody: () => <span>Aucun item disponible pour ce skin</span>,
				itemCreator: ItemCreator.setDefaultConfigFromReg(me.reg, {
					itemTypesTree: {
						itemTypeReducer: (acc: ItemType[], cur: ItemType) => {
							if (rootModelsKeys.has(cur.getModel())) acc.push(cur);
							return acc;
						}
					}
				}),
			} as OFastFindItemsInit);
			const wspTitle = me.testWsp.wspTitle;
			const selectedItem = await POPUP.showMenu<JSrcFields>(ct, {initHeight: "15em", viewPortY: "middle"}, this, {
				titleBar: {barLabel: {label: `Sélection de l'item de test dans l'atelier ${wspTitle}`}}
			}).onNextClose();

			if (selectedItem) return me.setTestItem(SRC.srcRef(selectedItem), selectedItem);
		} catch (e) {
			POPUP.showNotifError("Échec à la recherche de l'item", this);
			ERROR.log(e);
		}
	}

	/**
	 * Recherche et affecte l'atelier de test
	 * @param ev
	 * @private
	 */
	private async findTestWsp(this: HTMLButtonElement, ev: Event): Promise<void> {
		try {
			if (this.disabled) return;

			const me = DOMSH.findHost<ItemSkinClassStlTest>(this);
			const wspTypesDefList = await WSPPACK.listWspTypesDef(me.reg.env.universe.packServer, [me.skinClassStl.skinClass(me.reg.env.wsp)?.class], true);

			const wspTypeWhiteList: string[] = [];
			wspTypesDefList.forEach((entry) => wspTypeWhiteList.push(entry.key));
			const skin = me.skinClassStl.skinClass(me.reg.env.wsp).name;

			const selectedWsp = await POPUP.showMenuFromEvent<JWspInfoInList | null>(new WspsGrid().initialize({
				reg: me.reg,
				wspMatchFilter: (ctx) => {
					if (ctx.wspType && wspTypeWhiteList.includes(ctx.wspType.key)) return true;
					if (ctx.wspOptions) for (const wspOption of ctx.wspOptions)
						if (wspTypeWhiteList.includes(wspOption.key)) return true;
				},
				gridInit: {
					autoSelOnFocus: 'first',
					emptyBody: (): Node => <c-msg label={`Aucun atelier exploitant le skin '${skin}'`} level="info"/>
				},
				defaultAction: new Action<WspsGrid>().setExecute((ctx) => POPUP.findPopupableParent(ctx).close(ctx.getSelectedWsp())),
				defaultActionOnClick: true,
			}), ev || me, me, {}, {initMaxHeight: '50vh', initWidth: '10em'}).onNextClose();
			if (selectedWsp) await me.setTestWsp(selectedWsp.wspCd, selectedWsp.title);
		} catch (e) {
			POPUP.showNotifError("Échec à la recherche de l'atelier", this);
			await ERROR.log(e);
		}
	}

	private setMsg(msg: string, level?: 'info' | 'error' | 'warning' | 'valid' | null, isFatal: boolean = false) {
		this.msgElt.setCustomMsg(msg, level);
		if (this.contentElt) DOM.setHidden(this.contentElt, isFatal);
	}

	/**
	 * Persistance de la config de test dans le privateFolder
	 * @private
	 */
	private async saveItemDatas() {
		// FIXME : on enregistre et load ces datas dans une structure custom à la racine du privateFolder pour compat ODB (@see eu.scenari.wspodb.wsp.OdbWspDefinition.getPrivateDataFolder)
		// FIXME : refUri = this.reg.env.longDesc.srcUri
		const fields = await this.reg.env.wsp.wspServer.config.privateFolderUrl.fetchJson<JSrcFields>(IO.qs("cdaction", "PutSrc", "format", "JSON", "fields", "srcDt", "param", this.reg.env.wsp.code, "refUri", "", "privateUri", this.privateUri), {
			method: "POST",
			body: JSON.stringify({
				testWspCode: this.testWsp ? this.testWsp.code : null,
				testSrcRef: this.testItem ? SRC.srcRef(this.testItem) : null,
			} as OItemSkinClassPersisData),
			headers: {"Content-Type": "application/json"}
		});
	}

	private async loadItemDatas() {
		// FIXME : on enregistre et load ces datas dans une structure custom à la racine du privateFolder pour compat ODB (@see eu.scenari.wspodb.wsp.OdbWspDefinition.getPrivateDataFolder)
		// FIXME : refUri = this.reg.env.longDesc.srcUri
		if (!this._currentLoadRequest) {
			this._currentLoadRequest = this.reg.env.wsp.wspServer.config.privateFolderUrl.fetch(IO.qs("cdaction", "GetSrc", "format", "stream", "fields", "srcDt", "param", this.reg.env.wsp.code, "refUri", "", "privateUri", this.privateUri), 'json');
			const resp = await this._currentLoadRequest;
			if (resp.status === 200) {
				const lastSaveDatas = resp.asJson;
				await this.setTestWsp(lastSaveDatas.testWspCode);
				if (lastSaveDatas.testWspCode) await this.setTestItem(lastSaveDatas.testSrcRef);
			}
		}
	}

	private _currentLoadRequest: Promise<IResponse>;

	private initTestWspCtx(wspReg: IReg<IWspUiEnv>): void {
		/**
		 * Hack des actions de Génération :
		 * 	- fullUri du skin à tester
		 */
		wspReg.registerSvc('gen.action.generate', 1, async (wsp: Wsp, uiContext: HTMLElement, srcRef: srcRef, codeGenStack: string, props: Jsonisable, customFullUriDest?: string) => {
			await this.reg.env.wsp.wspServer.wspsLive.saveAllHouses();
			let transform = "transform=facet&facet=resolveSkin";
			props.skin = ITEM.GEN_SKIN_BY_URI_PREFIX + WSP.buildWspUri(this.reg.env.wsp.code, this.reg.env.longDesc.srcUri) + ";" + transform;
			return ITEM.generate(wsp, uiContext, srcRef, codeGenStack, props, customFullUriDest);
		});
		wspReg.registerSvc('gen.action.genInfo', 1, async (wsp: Wsp, uiContext: HTMLElement, srcRef: srcRef, codeGenStack: string, addExtraInfos?: boolean, customFullUriDest?: string): Promise<JGenInfo> => {
			let genInfo = await ITEM.fetchGenInfo(wsp, uiContext, srcRef, codeGenStack, addExtraInfos, customFullUriDest);
			genInfo.skins = null;// seul le skin à tester est proposé
			return genInfo;
		});

	}

	/** Mémoire du listener des WspChanges pour delete. */
	protected _onWspUriChange: (msg: JWspUriChangeMsg, from: WspsLivePlace | 'local' | 'server') => void;

	protected onWspUriChange(msg: JWspUriChangeMsg, from: WspsLivePlace | 'local' | 'server') {
		if (this.reg.env.longDesc) {
			const msgSrcRef = SRC.srcRef(msg);
			if (msg.type === EWspChangesEvts.r) {
				//Traitement du remove
				if (SRC.srcRef(this.reg.env.longDesc) === msgSrcRef || ITEM.getSrcUriType(msg.srcUri) === 'space' && SRC.isSubUri(msg.srcUri, this.reg.env.longDesc.srcUri)) {
					this.onViewHidden(true);
				}
			}
			/*else if (msg.type === EWspChangesEvts.u && SRC.srcRef(this.reg.env.longDesc) === msgSrcRef) {
				this.refreshUi();
			}*/
		}
	}

	visitViews(visitor: (view: IView) => any, options?: OViewVisitOptions): any {
		if (this.view) return visitor(this.view);
	}

	visitViewsAsync(visitor: (view: IView) => Promise<any>, options?: OViewVisitOptions): Promise<any> {
		return this.view ? visitor(this.view) : Promise.resolve(undefined);
	}

	onViewHidden(closed?: boolean) {
		this.saveItemDatas();
		if (closed) {
			this.reg.env.place.eventsMgr.removeListener('wspUriChange', this._onWspUriChange);
		}
		VIEWS.onViewHidden(this.view, closed);
	}

	onViewShown() {
		if (!this.view) this.loadItemDatas();
		else VIEWS.onViewShown(this.view);
	}

}

customElements.define("styler-item-testskin", ItemSkinClassStlTest);

REG.reg.registerSkin("styler-item-testskin", 1, /* language=CSS */ `
	:host {
		flex: 1;
		overflow: auto;
		display: flex;
		flex-direction: column;
	}

	:host > c-msg {
		max-width: 30rem;
		align-self: center;
		text-align: center;
	}

  #headBar {
	  display: flex;
	  align-items: center;
	  /*min-height: 1.5em;*/
	  min-width: 0;
	  border-bottom: 1px solid var(--border-color);
	  padding: .2em;
	  background-color: var(--bgcolor);
	  color: var(--color);
	  flex-wrap: wrap;
	  /*
		background-repeat: no-repeat;
		background-size: 1em;
		background-position: left .2em center;
		padding-inline-start: 1.5em;
		background-image: url("/@skin@/styler/views/testSkin.svg");
		*/
  }

  .headTi {
	  padding: 0 .3em;
	  white-space: nowrap;
	  color: var(--fade-color);
	  margin: .8rem 1rem 0 0;
  }

  .headSubTi {
	  font-size: .7rem;
	  color: var(--fade-color);
  }

  #ctxWsp, #ctxItem {
	  flex: 1;
	  min-width: 10em;
  }

  #levelSeparator::after {
	  content: ">";
	  color: var(--fade-color);
	  font-size: 1.5rem;
	  display: flex;
	  padding-inline: .5em;
	  padding-block-start: .8rem;
  }

  #ctxWsp > c-button, #ctxItem > c-button {
	  margin: 0;
  }

  c-button > wsp-src-drawer-inline {
	  overflow: hidden;
	  text-overflow: ellipsis;
  }

  c-button[data-isEmpty] {
	  font-style: italic;
  }

  c-button[data-isError] {
	  border-color: var(--error-color);
  }

  #contentBox {
	  background-color: var(--bgcolor);
	  flex: 1;
	  display: flex;
  }

  #contentBox[hidden] {
	  visibility: collapse;
  }

	#tabs {
		flex: 1;
	}

`);

REG.reg.registerSkin("styler-testSkin/tabs", 1, /* language=CSS */ `
	c-tab.selected {
		-webkit-text-stroke: unset !important;
	}
`);

REG.reg.registerSkin("styler-testSkin/tab", 1, /* language=CSS */ `
	.label {
		padding: 1rem 0;
		writing-mode: vertical-lr;
		/* text-orientation: sideways-right; */
		transform: rotate(
				180deg
		);
		/*webkit-text-strok : unset;*/
	}

`);
